import { useCallback, useEffect, useMemo, useState } from 'react';
import CollectorService from '../../services/collector-service';
import usePush from '../../hooks/usePush';

function useCollectorRateSync({ open, values, collectingInvoices, shortCode }) {
  const [collectingRate, setCollectingRate] = useState(0);
  const { countryCode, debtorType, state } = values;
  const stateForRate = countryCode === 'US' ? state : null;

  const { execute: updateRate, loading: pLoading } = usePush(
    CollectorService.collectorPriceCalculator,
    {
      errorToast: err => err.message,
      throwOnError: false,
    },
  );

  const canCalculateRate = useMemo(() => {
    if (!open) return false;
    if (!countryCode || !debtorType) return false;
    if (countryCode === 'US' && !state) return false;
    return collectingInvoices.length > 0;
  }, [open, countryCode, debtorType, state, collectingInvoices.length]);

  const refetchCollectorRate = useCallback(
    async (invoicesOverride = collectingInvoices) => {
      if (!canCalculateRate) return null;

      const invoiceIds = invoicesOverride.map(invoice => invoice._id);
      if (!invoiceIds.length) return null;

      const rateResponse = await updateRate({
        shortCode,
        invoiceIds,
        countryCode,
        debtorType,
        debtorAddress: {
          country: countryCode,
          state: stateForRate || null,
        },
      });

      const [err, res] = Array.isArray(rateResponse) ? rateResponse : [null, rateResponse];

      if (err || res == null) return null;

      setCollectingRate(res);
      return res;
    },
    [
      canCalculateRate,
      collectingInvoices,
      shortCode,
      updateRate,
      countryCode,
      debtorType,
      stateForRate,
    ],
  );

  useEffect(() => {
    if (!canCalculateRate) return;

    let active = true;
    const debounceTimeout = setTimeout(async () => {
      const result = await refetchCollectorRate();
      if (!active || result === null) return;
    }, 350);

    return () => {
      active = false;
      clearTimeout(debounceTimeout);
    };
  }, [canCalculateRate, refetchCollectorRate]);

  return {
    collectingRate,
    setCollectingRate,
    refetchCollectorRate,
    pLoading,
  };
}

export default useCollectorRateSync;
